import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendAuxiliarMessage, executeAuxiliarAction, transcribeAudio } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Bot, 
  Send, 
  Mic, 
  MicOff, 
  FileText, 
  ClipboardList,
  Truck,
  X,
  Loader2,
  Check,
  AlertCircle,
  Users,
  Briefcase,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

export const AuxiliarPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! Soy Auxiliar, tu asistente de facturación. Puedo ayudarte a crear facturas, presupuestos y albaranes. Por ejemplo:\n\n• "Hazme un presupuesto de 10 horas de consultoría para el cliente X"\n• "Crea una factura de desarrollo web para la empresa Y"\n• "Necesito un albarán de 5 unidades del producto Z"\n\n¿En qué puedo ayudarte?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Check microphone permission on mount
  useEffect(() => {
    checkMicPermission();
  }, []);

  const checkMicPermission = async () => {
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setMicPermission(result.state);
        result.onchange = () => setMicPermission(result.state);
      }
    } catch (e) {
      // Some browsers don't support permission query for microphone
      setMicPermission('unknown');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const { data } = await sendAuxiliarMessage(userMessage);
      const response = data.response;

      if (response.action === 'create_quote' || response.action === 'create_invoice' || response.action === 'create_delivery_note') {
        setPendingAction(response);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.message || `Voy a crear ${response.action === 'create_invoice' ? 'una factura' : response.action === 'create_quote' ? 'un presupuesto' : 'un albarán'} para ${response.client_name}. ¿Confirmas?`,
          action: response
        }]);
      } else if (response.action === 'create_client') {
        setPendingAction(response);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.message || `Voy a crear el cliente "${response.name}". ¿Confirmas?`,
          action: response
        }]);
      } else if (response.action === 'create_service') {
        setPendingAction(response);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.message || `Voy a crear el servicio "${response.name}" a ${response.hourly_rate}€/${response.unit || 'hora'}. ¿Confirmas?`,
          action: response
        }]);
      } else if (response.action === 'clarify') {
        setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: response.message || data.raw }]);
      }
    } catch (e) {
      toast.error('Error al procesar mensaje');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, hubo un error procesando tu solicitud.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteAction = async () => {
    if (!pendingAction) return;

    setLoading(true);
    try {
      const { data } = await executeAuxiliarAction(pendingAction);
      
      let successMessage = '';
      let createdInfo = null;
      
      if (pendingAction.action === 'create_invoice') {
        successMessage = `Factura ${data.invoice_number} creada correctamente.`;
        createdInfo = { type: 'create_invoice', id: data.id, number: data.invoice_number };
        toast.success('Factura creada');
      } else if (pendingAction.action === 'create_quote') {
        successMessage = `Presupuesto ${data.quote_number} creado correctamente.`;
        createdInfo = { type: 'create_quote', id: data.id, number: data.quote_number };
        toast.success('Presupuesto creado');
      } else if (pendingAction.action === 'create_delivery_note') {
        successMessage = `Albarán ${data.delivery_number} creado correctamente.`;
        createdInfo = { type: 'create_delivery_note', id: data.id, number: data.delivery_number };
        toast.success('Albarán creado');
      } else if (pendingAction.action === 'create_client') {
        successMessage = `Cliente "${data.name}" creado correctamente. Ahora puedes crear facturas para este cliente.`;
        toast.success('Cliente creado');
      } else if (pendingAction.action === 'create_service') {
        successMessage = `Servicio "${data.name}" creado a ${fmtEur(data.hourly_rate)}/${data.unit}. Ahora puedes usarlo en tus facturas.`;
        toast.success('Servicio creado');
      } else if (pendingAction.action === 'create_expense') {
        successMessage = `Gasto registrado: ${data.vendor || 'gasto'} por ${new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(data.total || 0)}.`;
        createdInfo = { type: 'create_expense', id: data.id };
        toast.success('Gasto registrado');
      } else if (pendingAction.action === 'create_contract') {
        successMessage = `Contrato creado: "${data.title}". Puedes verlo y editarlo en la sección de Contratos.`;
        createdInfo = { type: 'create_contract', id: data.id };
        toast.success('Contrato generado');
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: successMessage + ' ¿Necesitas algo más?',
        created: createdInfo
      }]);
      
      setPendingAction(null);
    } catch (e) {
      toast.error('Error al ejecutar acción');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error al ejecutar la acción. Por favor, inténtalo de nuevo.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAction = () => {
    setPendingAction(null);
    setMessages(prev => [...prev, { role: 'assistant', content: 'De acuerdo, he cancelado la acción. ¿En qué más puedo ayudarte?' }]);
  };

  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Tu navegador no soporta grabación de audio');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      
      // Check supported mime types
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        }
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        setLoading(true);
        try {
          const { data } = await transcribeAudio(blob);
          if (data.text) {
            setInput(data.text);
            toast.success('Audio transcrito');
          } else {
            toast.error('No se detectó audio');
          }
        } catch (e) {
          toast.error('Error al transcribir audio');
        } finally {
          setLoading(false);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success('Grabando... Habla ahora');
    } catch (e) {
      console.error('Microphone error:', e);
      setMicPermission('denied');
      
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        toast.error(
          <div>
            <p className="font-medium">Permiso de micrófono denegado</p>
            <p className="text-xs mt-1">Ve a Ajustes del navegador → Permisos → Micrófono y permite el acceso</p>
          </div>,
          { duration: 5000 }
        );
      } else if (e.name === 'NotFoundError') {
        toast.error('No se encontró ningún micrófono');
      } else {
        toast.error('No se pudo acceder al micrófono');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create_invoice': return <FileText className="w-4 h-4" />;
      case 'create_quote': return <ClipboardList className="w-4 h-4" />;
      case 'create_delivery_note': return <Truck className="w-4 h-4" />;
      case 'create_client': return <Users className="w-4 h-4" />;
      case 'create_service': return <Briefcase className="w-4 h-4" />;
      case 'create_expense': return <Receipt className="w-4 h-4" />;
      case 'create_contract': return <FileText className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="h-[calc(100vh-180px)] lg:h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center ai-glow flex-shrink-0">
          <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Auxiliar</h1>
          <p className="text-stone-500 text-xs sm:text-sm">Tu asistente de facturación con IA</p>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden flex flex-col">
        <ScrollArea className="flex-1 p-3 sm:p-6" ref={scrollRef}>
          <div className="space-y-4 sm:space-y-6">
            {messages.map((message, index) => (
              <div
                key={`msg-${message.role}-${index}`}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[90%] sm:max-w-[80%] ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-[#FF6B4A] to-[#E05A3E] text-white rounded-2xl rounded-br-none'
                      : 'bg-stone-100 text-stone-900 rounded-2xl rounded-bl-none'
                  } px-3 sm:px-4 py-2.5 sm:py-3`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  
                  {/* Action preview */}
                  {message.action && (
                    <div className="mt-3 p-3 bg-white/10 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        {getActionIcon(message.action.action)}
                        <span className="text-xs font-medium uppercase tracking-wider">
                          {message.action.action === 'create_invoice' ? 'Factura' : 
                           message.action.action === 'create_quote' ? 'Presupuesto' :
                           message.action.action === 'create_expense' ? 'Gasto' : 
                           message.action.action === 'create_client' ? 'Cliente' :
                           message.action.action === 'create_service' ? 'Servicio' :
                           message.action.action === 'create_contract' ? 'Contrato' : 'Albarán'}
                        </span>
                      </div>
                      {message.action.client_name && <p className="text-xs opacity-80">Cliente: {message.action.client_name}</p>}
                      {message.action.vendor && <p className="text-xs opacity-80">Proveedor: {message.action.vendor} · Total: {fmtEur(message.action.total)}</p>}
                      {message.action.prompt && <p className="text-xs opacity-80">Tipo: {message.action.prompt?.substring(0, 60)}...</p>}
                      {message.action.name && !message.action.vendor && !message.action.prompt && <p className="text-xs opacity-80">Nombre: {message.action.name}</p>}
                      {message.action.items && (
                        <div className="mt-2 text-xs opacity-80">
                          {message.action.items.map((item, i) => (
                            <p key={i}>{item.quantity}x {item.description} - {fmtEur(item.unit_price)}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Created document link */}
                  {message.created && (
                    <button
                      onClick={() => {
                        const path = message.created.type === 'create_invoice' ? 'invoices' :
                                    message.created.type === 'create_quote' ? 'quotes' : 
                                    message.created.type === 'create_expense' ? 'expenses' :
                                    message.created.type === 'create_contract' ? 'contracts' : 'delivery-notes';
                        if (message.created.type === 'create_expense') {
                          navigate(`/${path}`);
                        } else {
                          navigate(`/${path}/${message.created.id}`);
                        }
                      }}
                      className="mt-2 flex items-center gap-2 text-xs bg-white/20 px-2 py-1 rounded-xl hover:bg-white/30 transition-colors"
                    >
                      {getActionIcon(message.created.type)}
                      {message.created.number ? `Ver ${message.created.number}` : 'Ver gastos'}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-stone-100 text-stone-900 rounded-xl rounded-bl-none px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Pending Action Buttons */}
        {pendingAction && (
          <div className="px-3 sm:px-6 py-3 bg-[#FF6B4A]/5 border-t border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-sm text-stone-600">¿Confirmas la creación?</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelAction}
                className="rounded-xl"
                data-testid="cancel-action-button"
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleExecuteAction}
                className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
                disabled={loading}
                data-testid="confirm-action-button"
              >
                <Check className="w-4 h-4 mr-1" />
                Confirmar
              </Button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-3 sm:p-4 border-t border-stone-100">
          {/* Mic permission warning */}
          {micPermission === 'denied' && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Micrófono bloqueado</p>
                <p className="text-amber-700 text-xs mt-0.5">
                  Para usar comandos de voz, permite el acceso al micrófono en los ajustes de tu navegador. 
                  Busca el icono de candado 🔒 en la barra de direcciones.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              className={`p-3 rounded-xl transition-all relative ${
                isRecording 
                  ? 'bg-red-500 text-white recording-pulse' 
                  : micPermission === 'denied'
                    ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              disabled={loading}
              title={micPermission === 'denied' ? 'Permite el acceso al micrófono en ajustes del navegador' : isRecording ? 'Parar grabación' : 'Grabar mensaje de voz'}
              data-testid="voice-button"
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje o usa el micrófono..."
              className="flex-1 rounded-xl"
              disabled={loading}
              data-testid="auxiliar-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
              data-testid="send-button"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-stone-400 mt-2 text-center">
            Pulsa el micrófono para dictar tu mensaje por voz
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuxiliarPage;
